import { marketingStyles } from 'views/ai_creative_studio/configs/marketingStyles';

export const CREATIVE_STUDIO_WHITELIST = [
    'minimalist_clean',
    'bold_typography',
    'gradient_vibrant',
    'glassmorphism',
    'neo_brutalism',
    'photorealistic',
    'flat_material',
    '3d_claymorphism',
    'editorial_cinematic',
    'bento_grid',
    'duotone',
    'photo_overlay',
    'magazine_cover',
    'split_screen',
    'dark_luxury',
    'text_container',
    'bold_statement',
];

export const PROMPT_GENERATION_SCHEMA = {
    name: 'prompt_generation',
    schema: {
        type: 'object',
        properties: {
            prompt: {
                type: 'string',
                description: 'The generated image prompt',
            },
        },
        required: ['prompt'],
        additionalProperties: false,
    },
    strict: true,
};

export const pickRandomMarketingStyles = (n = 5) => {
    const pool = marketingStyles.filter(
        s => CREATIVE_STUDIO_WHITELIST.includes(s.key) && s.promptModifier
    );
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(n, shuffled.length));
};

export const buildCreativeImagePromptMeta = ({
    idea,
    useBrand,
    companyName,
    count = 5,
}) => {
    const styles = pickRandomMarketingStyles(count);

    const brandBlock = useBrand && companyName
        ? `Brand context: align the visual mood, color palette, and tone with ${companyName}'s identity, but do NOT include the company name, logo, or any personal names in the rendered image.`
        : '';

    const ideaBlock = idea
        ? `Topic / idea: "${idea}"`
        : '';

    const styleList = styles
        .map(s => `- ${s.name}: ${s.promptModifier}`)
        .join('\n');

    return `
You are a creative director writing an image-generation prompt for an
agency-grade marketing creative.

${brandBlock}
${ideaBlock}

Pick ONE of these visual styles that best fits the topic, then write a
single dense image-generation prompt that fully embodies its modifier:

${styleList}

REQUIREMENTS
- Full-frame, edge-to-edge composition. No mockups, no device frames,
  no image-in-image, no posters-within-posters.
- The chosen style's language must dominate — typography, composition,
  color, texture as specified by the modifier.
- If a headline fits the topic, integrate concrete short headline copy
  (5–10 words) as part of the visual description.
- 60–140 words. Comma/sentence-separated visual phrases. No preamble.
- Do NOT mention which preset you chose, do NOT use jargon like
  "background_override", "position 1", "cover image" or "carousel".
- Do NOT include brand names, company names, URLs, logos, or personal
  names in the rendered image.

Return ONLY the prompt text in the \`prompt\` field.
`.trim();
};

export const cleanGeneratedPrompt = (response) => {
    let promptText = response;
    if (typeof response === 'string') {
        try {
            const parsed = JSON.parse(response);
            promptText = parsed.prompt || response;
        } catch {
            promptText = response;
        }
    } else if (response?.prompt) {
        promptText = response.prompt;
    }

    return (promptText || '')
        .replace(/^["']|["']$/g, '')
        .replace(/\\n/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};
